import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicShell from "@/components/PublicShell";
import { GoldDivider, LuxuryCard } from "@/components/luxury";
import { POSTS } from "@/content/journal";
import { ClockIcon as Clock, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { Icon } from "@/components/luxury";
import api from "@/lib/api";

const SITE_NAME = "Armstrong K9 Registry";
const PAGE_TITLE = "Journal · Armstrong K9 Registry";
const PAGE_DESC =
  "Editorial notes on what we verify, how we verify it, and what buyers actually need to read on a pedigree before they trust it. Written by the Armstrong K9 team — not sponsored, not paid placement.";

/**
 * Editorial journal index.
 *
 * Merges two sources of posts in newest-first order:
 *   1. Static JSX posts in /content/journal — curated long-form
 *      editorials that may use platform components (WaxSeal, gold
 *      dividers, photos).
 *   2. DB-backed posts from /api/journal/posts — written through the
 *      admin CMS at /admin/journal, Markdown-bodied, rendered via
 *      JournalPost when opened.
 *
 * The featured tile is whichever post is newest across both sources.
 * Public route — no auth — so it indexes cleanly.
 */
export default function Journal() {
  const [dbPosts, setDbPosts] = useState([]);
  const [footerDisclaimer, setFooterDisclaimer] = useState("");

  useEffect(() => {
    // Pull published posts from the CMS. Silent failure is fine — the
    // static editorials still render so the page never goes blank.
    api
      .get("/journal/posts")
      .then((r) => setDbPosts(Array.isArray(r.data?.posts) ? r.data.posts : []))
      .catch(() => setDbPosts([]));
    api
      .get("/journal/settings")
      .then((r) => setFooterDisclaimer(r.data?.footer_disclaimer || ""))
      .catch(() => setFooterDisclaimer(""));
  }, []);

  // Normalize both sources into a common shape for sorting + rendering.
  const staticEntries = POSTS.map((p) => ({
    slug: p.slug,
    title: p.title,
    dek: p.dek,
    section: p.section,
    readMinutes: p.readMinutes,
    publishedAt: p.publishedAt,
    source: "static",
  }));
  const dbEntries = (dbPosts || []).map((p) => ({
    slug: p.slug,
    title: p.title,
    dek: p.dek,
    section: p.section,
    readMinutes: p.read_minutes || 1,
    publishedAt: (p.published_at || p.updated_at || p.created_at || "").slice(0, 10),
    source: "db",
  }));

  const sorted = [...staticEntries, ...dbEntries].sort((a, b) =>
    (b.publishedAt || "").localeCompare(a.publishedAt || ""),
  );
  const [featured, ...rest] = sorted;

  // JSON-LD: a Blog with an itemList of the latest posts. Google reads
  // this to surface the journal as a content collection in rich
  // results. Built client-side because the URL is dynamic per-env.
  const origin = typeof window !== "undefined" ? webOrigin() : "";
  const blogLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: PAGE_TITLE,
    description: PAGE_DESC,
    url: `${origin}/journal`,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${origin}/logo-512.png` },
    },
    blogPost: sorted.slice(0, 10).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${origin}/journal/${p.slug}`,
      datePublished: p.publishedAt,
      articleSection: p.section,
    })),
  };

  return (
    <PublicShell>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={`${origin}/journal`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={`${origin}/journal`} />
        <meta property="og:image" content={`${origin}/logo-512.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
        <meta name="twitter:image" content={`${origin}/logo-512.png`} />
        <script type="application/ld+json">{JSON.stringify(blogLd)}</script>
      </Helmet>
      {/* Header */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-12 pb-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Journal</p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Notes from the registry
        </h1>
        <p className="text-[#6B6B6B] mt-4 max-w-2xl leading-relaxed">
          Editorial notes on what we verify, how we verify it, and what
          buyers actually need to read on a pedigree before they trust
          it. Written by the team. Not sponsored, not paid placement.
        </p>
        <GoldDivider className="mt-10" />
      </div>

      {/* Featured post */}
      {featured && (
        <section className="max-w-5xl mx-auto px-6 md:px-10 pb-12" data-testid="journal-featured">
          <Link to={`/journal/${featured.slug}`} className="group block" data-testid={`journal-featured-${featured.slug}`}>
            <LuxuryCard interactive className="p-8 md:p-10">
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] font-bold text-gold mb-3">
                <span>Featured</span>
                <span className="text-[#9CA3AF] tracking-normal font-medium normal-case">
                  · {featured.section}
                </span>
              </div>
              <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.1] group-hover:text-crimson transition-colors">
                {featured.title}
              </h2>
              <p className="mt-5 text-[#3a3a3a] leading-relaxed max-w-2xl text-[15px]">
                {featured.dek}
              </p>
              <div className="gold-hairline mt-7 mb-5 max-w-md" />
              <div className="flex items-center justify-between text-xs text-[#6B6B6B] font-tabular">
                <span>{featured.readMinutes}-minute read</span>
                <span className="inline-flex items-center gap-1.5 text-ink group-hover:text-crimson transition-colors font-medium not-italic">
                  Read the full piece
                  <Icon as={ArrowRight} tone="current" size="xs" weight="bold" />
                </span>
              </div>
            </LuxuryCard>
          </Link>
        </section>
      )}

      {/* Rest of the posts */}
      {rest.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 md:px-10 pb-20" data-testid="journal-grid">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B6B6B] mb-5">More from the journal</h3>
          <div className="grid md:grid-cols-2 gap-5">
            {rest.map((post) => (
              <Link key={post.slug} to={`/journal/${post.slug}`} className="group block" data-testid={`journal-card-${post.slug}`}>
                <LuxuryCard interactive className="p-6 h-full flex flex-col">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] font-bold text-gold mb-3">
                    <span>{post.section}</span>
                  </div>
                  <h4 className="font-spectral text-2xl tracking-tight leading-tight group-hover:text-crimson transition-colors">
                    {post.title}
                  </h4>
                  <p className="text-sm text-[#3a3a3a] mt-3 leading-relaxed flex-1">
                    {post.dek}
                  </p>
                  <div className="gold-hairline mt-5 mb-3" />
                  <div className="flex items-center gap-2 text-xs text-[#6B6B6B] font-tabular">
                    <Icon as={Clock} tone="muted" size="xs" weight="duotone" />
                    {post.readMinutes}-min read
                  </div>
                </LuxuryCard>
              </Link>
            ))}
          </div>
        </section>
      )}
      {/* Standing footer disclaimer — admin-editable from /admin/journal/settings */}
      {footerDisclaimer && (
        <section className="max-w-5xl mx-auto px-6 md:px-10 pb-16" data-testid="journal-footer-disclaimer">
          <div className="gold-hairline mb-4 max-w-md mx-auto" />
          <p className="text-[11px] leading-relaxed text-[#6B6B6B] italic max-w-2xl mx-auto text-center">
            {footerDisclaimer}
          </p>
        </section>
      )}
    </PublicShell>
  );
}
